import { Info } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

/**
 * Lightweight info-pill explainer used to clarify a tab's purpose.
 * Same visual pattern as HealthScoreExplainer.
 *
 * Props:
 *   - label:    short text on the pill (default "About this tab")
 *   - title:    headline inside the popover (e.g. "What is Documents?")
 *   - intro:    short paragraph beneath the title (1-2 sentences)
 *   - useFor:   list of bullets describing what the tab is for
 *   - notFor:   optional bullets clarifying what it is NOT for
 *   - useCases: optional list of concrete examples
 *   - testId:   override for data-testid (default 'tab-explainer-trigger')
 *   - size:     'sm' | 'md' icon size
 *   - inline:   if true, render without the rounded-pill border
 */
export default function TabExplainer({
  label = 'About this tab',
  title,
  intro,
  useFor = [],
  notFor = [],
  useCases = [],
  testId,
  size = 'md',
  inline = false,
}) {
  const iconSize = size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4';

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={title || label}
          data-testid={testId || 'tab-explainer-trigger'}
          className={cn(
            'inline-flex items-center gap-1.5 text-xs text-[#5D524D] hover:text-[#2C2420] transition-colors',
            !inline && 'px-2 py-1 rounded-full border border-[#E6E0D9] hover:border-[#4A3B32]/40 bg-white'
          )}
        >
          <Info className={iconSize} />
          {label && <span className="hidden sm:inline">{label}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-80 p-0 border-[#E6E0D9] shadow-lg"
        data-testid={`${testId || 'tab-explainer'}-popover`}
      >
        {title && (
          <div className="p-4 border-b border-[#E6E0D9]">
            <p className="font-heading text-sm text-[#2C2420] mb-1">{title}</p>
            {intro && (
              <p className="text-xs text-[#5D524D] leading-relaxed">{intro}</p>
            )}
          </div>
        )}

        {useFor.length > 0 && (
          <div className="p-4 border-b border-[#E6E0D9]">
            <p className="text-[11px] uppercase tracking-wide text-[#8C8078] mb-2">Use this for</p>
            <ul className="space-y-1.5 text-xs text-[#2C2420]">
              {useFor.map((line, i) => (
                <li key={i} className="flex items-baseline gap-2">
                  <span className="text-[#A86A3A] mt-0.5">•</span>
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {notFor.length > 0 && (
          <div className="p-4 border-b border-[#E6E0D9]">
            <p className="text-[11px] uppercase tracking-wide text-[#8C8078] mb-2">Not for</p>
            <ul className="space-y-1.5 text-xs text-[#5D524D]">
              {notFor.map((line, i) => (
                <li key={i} className="flex items-baseline gap-2">
                  <span className="text-[#8C8078] mt-0.5">·</span>
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {useCases.length > 0 && (
          <div className="p-4">
            <p className="text-[11px] uppercase tracking-wide text-[#8C8078] mb-2">Examples</p>
            <ul className="space-y-1 text-xs text-[#2C2420]">
              {useCases.map((line, i) => (
                <li key={i} className="text-[#5D524D]">&ldquo;{line}&rdquo;</li>
              ))}
            </ul>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
